import React, { useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import upperFirst from 'lodash/upperFirst';

import SelectDropdown from 'components/uiLibrary/Inputs/SelectDropdown';
import Checkbox from 'components/uiLibrary/Inputs/Checkbox';
import Typography from 'components/uiLibrary/Typography';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Drawer from 'components/uiLibrary/Drawer';

import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { useTranslation } from 'src/i18n';

import { TP } from 'constants/index';

import classes from './MultiSelectDropdown.module.scss';

const RenderOption = ({ option, isChecked, onSelect, onUnselect, label }) => {
  const { t } = useTranslation('NS_DISPLAY_V4');
  const helperText = upperFirst(label === 'Country' ? `${t(`${TP}.FN_PRODUCTIONS`)}` : '')?.trim();

  return (
    <div className={classes.option} key={option?.id}>
      <Checkbox
        onChange={() => {
          if (isChecked) {
            onUnselect(option);
          } else {
            onSelect(option);
          }
        }}
        label={
          <span className={classes.checkbox__label}>
            {option?.label}
            {option?.total > 0 ? (
              <Typography color="secondary" size={12}>
                {option?.total} {helperText}
              </Typography>
            ) : (
              ''
            )}
          </span>
        }
        checked={isChecked}
        name={option?.id}
        className={classes.checkbox}
        size="small"
      />
    </div>
  );
};

const RenderValue = ({ selectedOptions }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const label = useMemo(() => {
    if (selectedOptions?.length > 0) {
      const name = selectedOptions?.[0]?.label;
      const count = selectedOptions.length > 1 ? ` (+${selectedOptions.length - 1})` : '';

      if (name) {
        return `${name}${count}`;
      }
    }

    return t(`${TP}.FN_FILTER_ALL`);
  }, [t, selectedOptions]);

  return <Typography size="14">{label}</Typography>;
};

const Options = ({ options, selectedOptions, onSelect, onUnselect, label }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const total = useMemo(() => options?.reduce((acc, item) => acc + item?.total, 0), [options]);

  return (
    <>
      <RenderOption
        key="all"
        isChecked={selectedOptions?.length === 0}
        option={{ id: -1, label: t(`${TP}.FN_FILTER_ALL`), ...(total && { total }) }}
        onUnselect={onUnselect}
        onSelect={onSelect}
      />
      {options?.map(item => (
        <RenderOption
          key={item?.id}
          isChecked={!!selectedOptions?.find(({ id }) => id === item?.id)}
          option={item}
          onUnselect={onUnselect}
          onSelect={onSelect}
          label={label}
        />
      ))}
    </>
  );
};

const MultiSelectDropdown = ({
  label,
  options = [],
  selectedOptions = [],
  disabled = false,
  onChange,
  styles,
  prefixContent,
  trackingData = {},
  mobileDrawerProps = {
    allowMobileDrawer: false,
    renderMobileOptions: [],
  },
  errorMessage,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const ref = useRef();
  const { allowMobileDrawer, drawerHeaderTitle, drawerHeaderSubtitle } = mobileDrawerProps || {};

  useOnClickOutside(ref, event => {
    const key = label || '';
    if (event?.target?.parentNode?.id === `menu-${key}`) {
      setIsExpanded(false);
    }
  });

  const onUnselect = option => {
    if (onChange && option?.id) {
      onChange(selectedOptions?.filter(item => item?.id !== option?.id) || []);
    }
  };

  const onSelect = option => {
    if (onChange) {
      if (option?.id === -1) {
        onChange([]);
      } else if (onChange && option?.id) {
        onChange([...selectedOptions, option]);
      }
    }
  };

  const iconComponent = useMemo(
    () => <SpriteIcon icon={isExpanded ? 'keyboard_arrow_up' : 'expand_more'} size={20} className={classes.icon} />,
    [isExpanded],
  );

  return (
    <>
      <SelectDropdown
        name={label}
        options={options}
        label={label}
        styles={{
          formControl: classnames(classes.formControl, { [styles?.formControl]: !!styles?.formControl }),
          select: classnames(classes.select, { [styles?.select]: !!styles?.select }),
          label: classnames(classes.label, { [styles?.label]: !!styles?.label }),
          paper: classnames(classes.paper, { [styles?.paper]: !!styles?.paper }),
        }}
        disabled={disabled}
        onOpen={() => {
          if (allowMobileDrawer) {
            setIsDrawerOpen(true);
          } else {
            setIsExpanded(true);
          }
        }}
        isOpen={isExpanded}
        value={selectedOptions?.length > 0 ? selectedOptions : [1]}
        renderValue={value => <RenderValue selectedOptions={value} />}
        size="small"
        trackingData={trackingData}
        iconComponent={iconComponent}
      >
        {({ options: renderOptions }) => (
          <div ref={ref}>
            {errorMessage || (
              <>
                {prefixContent && (
                  <div
                    className={classnames(classes.prefixContent, {
                      [styles?.prefixContent]: !!styles?.prefixContent,
                    })}
                  >
                    {prefixContent}
                  </div>
                )}
                <Options
                  options={renderOptions}
                  selectedOptions={selectedOptions}
                  onSelect={onSelect}
                  onUnselect={onUnselect}
                  label={label}
                />
              </>
            )}
          </div>
        )}
      </SelectDropdown>
      {allowMobileDrawer && (
        <Drawer
          // subTitle={drawerHeaderSubtitle}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          className={classes.drawerContainer}
        >
          <div className={classes.drawerContainer__header}>
            <SpriteIcon icon="arrow_back" size="16" onClick={() => setIsDrawerOpen(false)} />
            <Typography weight="bold" className={classes.drawerContainer__header_title}>
              {drawerHeaderTitle}
            </Typography>
            <SpriteIcon icon="close" size="16" onClick={() => setIsDrawerOpen(false)} />
          </div>
          <Options
            options={options}
            selectedOptions={selectedOptions}
            onSelect={onSelect}
            onUnselect={onUnselect}
            label={label}
          />
        </Drawer>
      )}
    </>
  );
};

export default MultiSelectDropdown;
